'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import FormCheckbox from '../../../../components/form/FormCheckbox/FormCheckbox';
import ModalBase from '../../../../components/ui/ModalBase/ModalBase';
import layoutStyles from '../../../../styles/modules/layout.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';

export interface SetNewsLetterModalProps {
  enableNewsLetterPermission: boolean;
  onSubmit: (permission: number) => Promise<void>;
  onError: () => void;
  getCookieValue: (key: string) => string | null;
  setCookie: (key: string, value: string, expires: number) => void;
  pushDataLayer: (data: Record<string, unknown>) => void;
}

function SetNewsLetterModal({
  enableNewsLetterPermission,
  onSubmit,
  onError,
  getCookieValue,
  setCookie,
  pushDataLayer,
}: SetNewsLetterModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getValues, handleSubmit, control } = useForm({
    mode: 'onChange',
    defaultValues: {
      setNewsLetterPermission: ['setNewsLetterPermission'],
    },
  });
  const cookieKey = 'recentOpenNewsLetterModal';

  useEffect(() => {
    const recentOpenNewsLetterModal = getCookieValue(cookieKey);
    if (!recentOpenNewsLetterModal && !enableNewsLetterPermission) {
      // カスタムイベント
      pushDataLayer({
        event: 'modal_view',
        modalName: 'mail_magazine_opt_in',
      });
      setIsOpen(true);
    }
  }, [enableNewsLetterPermission, getCookieValue, pushDataLayer]);

  const submit = async () => {
    try {
      setIsLoading(true);
      const checked = getValues('setNewsLetterPermission').includes('setNewsLetterPermission');
      // カスタムイベント
      pushDataLayer({
        event: 'modal_button_click',
        buttonName: 'mail_magazine_opt_in_button',
        checkboxChecked: checked ? 'true' : 'false',
      });
      await onSubmit(checked ? 2 : 0);
      // 30日後に再表示
      setCookie(cookieKey, 'true', 60 * 60 * 24 * 30);
      setIsOpen(false);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const submitError = () => {
    onError();
  };

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      showCloseButton={false}
    >
      <div className={layoutStyles.modalPadding}>
        <div className={layoutStyles.modalTitle}>
          本サービスご利用にあたっての
          <br />
          ユーザ情報の登録
        </div>
        <p className={`${utilsStyles.mBSmall} ${utilsStyles.textXSmall}`}>
          ご登録メールアドレス宛にお得なキャンペーンなどの情報をお送りいたします。不要な方は下記チェックを外してください。
        </p>
        <FormCheckbox
          name='setNewsLetterPermission'
          items={[
            {
              value: 'setNewsLetterPermission',
              child: <>お得なキャンペーン情報を受けとる</>,
            },
          ]}
          control={control}
        />
        <div className={utilsStyles.mtSmall}>
          <ActionButton
            isLoading={isLoading}
            onClick={handleSubmit(submit, submitError)}
            size='fullWidth'
            color='primary'
          >
            登録
          </ActionButton>
        </div>
      </div>
    </ModalBase>
  );
}

export default SetNewsLetterModal;
